import { ReactNode, useEffect, useState } from "react";
import {
    AnalysisOutput,
    Project,
    ProjectAnalysis,
} from "../../types/ProjectTypes";
import { getAnalysisOutput } from "../../common/api/ProjectClient";
import { serializeAnalysisOutput } from "./serializeAnalysisOutput";
import { DrawerAssistant } from "./DrawerAssistant";

interface Options {
    project: Project;
    analysis: ProjectAnalysis;
    /**
     * The already-loaded analysis output, if the host component has it (the
     * Deal and RFX views fetch it to render). When provided it is serialized
     * directly; when omitted (the Flexible view renders via an iframe and never
     * loads the output object) the output is lazily fetched the first time the
     * drawer opens, so we don't pay for it unless the user asks.
     */
    output?: AnalysisOutput;
}

interface AnalysisAssistant {
    /** Whether the assistant is available for this analysis. */
    showAskButton: boolean;
    /** Open the assistant drawer. */
    openAssistant: () => void;
    /** The drawer element to render (null when the assistant is unavailable). */
    assistantDrawer: ReactNode;
}

/**
 * Shared wiring for the report AI assistant: open state, lazy
 * serialization of the analysis into grounding content, and the drawer itself.
 * Each output view (Flexible / Deal / RFX) renders its own "Ask" affordance and
 * calls {@link AnalysisAssistant.openAssistant}; the serialization and backend
 * call are identical across formats.
 */
export function useAnalysisAssistant({ project, analysis, output }: Options): AnalysisAssistant {
    const [assistantOpen, setAssistantOpen] = useState(false);
    const [analysisContent, setAnalysisContent] = useState<string | undefined>(undefined);

    const showAskButton = !!analysis.outputType;

    // Pre-loaded path: serialize the output the host already fetched. Kept in
    // sync if the output prop changes (e.g. a newer analysis version loads).
    useEffect(() => {
        if (output === undefined) return;
        setAnalysisContent(serializeAnalysisOutput(output));
    }, [output]);

    // Lazy path (no pre-loaded output): fetch + serialize the first time the
    // drawer opens.
    useEffect(() => {
        if (output !== undefined) return;
        if (!assistantOpen || analysisContent !== undefined || !analysis.outputType) return;
        let cancelled = false;
        getAnalysisOutput(project.id, analysis.version)
            .then((fetched) => {
                if (!cancelled) setAnalysisContent(serializeAnalysisOutput(fetched));
            })
            .catch(() => {
                // Leave the assistant ungrounded if the output can't be loaded.
            });
        return () => {
            cancelled = true;
        };
    }, [output, assistantOpen, analysisContent, analysis.outputType, analysis.version, project.id]);

    const assistantDrawer = showAskButton ? (
        <DrawerAssistant
            open={assistantOpen}
            onClose={() => setAssistantOpen(false)}
            projectId={project.id}
            projectName={project.name}
            vendorName={project.orderForm?.vendor?.name}
            analysisVersions={[analysis.version]}
            analysisContent={analysisContent}
        />
    ) : null;

    return {
        showAskButton,
        openAssistant: () => setAssistantOpen(true),
        assistantDrawer,
    };
}
